import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { ApiError, request } from '../../lib/api-client.ts';
import { formatJst } from '../../lib/format.ts';
import type { CredentialRecord } from '../../types/credential.ts';

type Props = {
  record: CredentialRecord;
  onUpdated: () => void;
};

type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; cleared: boolean }
  | { kind: 'error'; message: string };

const RESULT_AUTO_RESET_MS = 4000;

/**
 * credential 1行の編集UI。
 *
 * 入力欄は password type（伏せ字）。保存ボタン押下で PUT /api/admin/credentials/:key。
 * 入力欄を空のままで保存すると値クリア（NULL に戻る）。
 */
export const CredentialRow = ({ record, onUpdated }: Props): React.JSX.Element => {
  const [value, setValue] = useState('');
  const [status, setStatus] = useState<SaveStatus>({ kind: 'idle' });
  const mountedRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const scheduleReset = (delay: number): void => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      if (mountedRef.current) setStatus({ kind: 'idle' });
    }, delay);
  };

  const save = async (): Promise<void> => {
    setStatus({ kind: 'saving' });
    try {
      const data = await request<{ key: string; cleared: boolean }>(
        `/api/admin/credentials/${encodeURIComponent(record.key)}`,
        {
          method: 'PUT',
          body: { value },
        }
      );
      if (!mountedRef.current) return;
      setStatus({ kind: 'saved', cleared: data.cleared });
      setValue('');
      onUpdated();
      scheduleReset(RESULT_AUTO_RESET_MS);
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err instanceof ApiError ? err.message : '保存に失敗しました';
      setStatus({ kind: 'error', message });
      scheduleReset(RESULT_AUTO_RESET_MS);
    }
  };

  return (
    <div style={rowStyle}>
      <div style={metaStyle}>
        <strong style={keyStyle}>{record.key}</strong>
        {record.description && <small style={descStyle}>{record.description}</small>}
        <small style={statusLineStyle}>
          {record.is_set ? (
            <>
              現在: <code style={maskedStyle}>{record.masked_value ?? '****'}</code>
              {' / '}更新: {formatJst(record.updated_at)}
              {record.updated_by ? ` by ${record.updated_by}` : ''}
            </>
          ) : (
            <span style={notSetStyle}>未設定</span>
          )}
        </small>
      </div>
      <form
        style={actionStyle}
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          if (status.kind !== 'saving') void save();
        }}
      >
        {/* Chrome のフォーム警告を消すためにダミーのユーザー名フィールドを置く（display:none） */}
        <input type="text" name="username" autoComplete="username" style={hiddenInputStyle} aria-hidden="true" tabIndex={-1} />
        <input
          type="password"
          name={`credential_${record.key}`}
          value={value}
          placeholder={record.is_set ? '更新する場合のみ入力（空で保存=クリア）' : '値を入力'}
          onChange={(e) => setValue(e.target.value)}
          style={inputStyle}
          disabled={status.kind === 'saving'}
          autoComplete="new-password"
          spellCheck={false}
          autoCapitalize="off"
        />
        <button type="submit" disabled={status.kind === 'saving'} style={primaryBtnStyle}>
          {status.kind === 'saving' ? '保存中…' : '保存'}
        </button>
        {status.kind === 'saved' && (
          <span style={successStyle}>
            {status.cleared ? 'クリアしました' : '保存しました'}
          </span>
        )}
        {status.kind === 'error' && <span style={errorStyle}>{status.message}</span>}
      </form>
    </div>
  );
};

const hiddenInputStyle: React.CSSProperties = {
  position: 'absolute',
  left: -9999,
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '14px 16px',
  borderBottom: '1px solid #eee',
};
const metaStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2 };
const keyStyle: React.CSSProperties = { fontSize: 13, fontFamily: 'monospace', color: '#222' };
const descStyle: React.CSSProperties = { color: '#666', fontSize: 12 };
const statusLineStyle: React.CSSProperties = { color: '#888', fontSize: 12 };
const maskedStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  background: '#f4f4f4',
  padding: '1px 4px',
  borderRadius: 2,
};
const notSetStyle: React.CSSProperties = { color: '#c0392b' };
const actionStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
};
const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 240,
  padding: '6px 8px',
  fontSize: 13,
  border: '1px solid #d0d0d0',
  borderRadius: 4,
};
const primaryBtnStyle: React.CSSProperties = { padding: '6px 14px', fontSize: 13, cursor: 'pointer' };
const successStyle: React.CSSProperties = { color: '#27ae60', fontSize: 13 };
const errorStyle: React.CSSProperties = { color: '#c0392b', fontSize: 13 };
